import { useEffect } from 'react';
import homeContent from './home-content.json';
import { trackPageView, trackAppDownload } from './analytics';

const appStoreUrl = 'https://apps.apple.com/app/apple-store/id6746115791?pt=124269742&ct=Web%20Page&mt=8';
const languageLinks = [
  ['/', 'English'],
  ['/tr', 'Türkçe'],
  ['/de', 'Deutsch'],
  ['/ar', 'العربية'],
  ['/fr', 'Français'],
  ['/pt', 'Português'],
  ['/es', 'Español'],
  ['/it', 'Italiano'],
];

const appScreenshots = [
  {
    src: '/app-screenshot-daily.webp',
    alt: 'LastFollow App Store screenshot: a tracked Instagram profile with follower and following counts and the Recent Followers and Recent Followings tabs',
    title: 'Who joined, who left',
    text:
      'Each daily check is compared against the stored list, and the difference is the report. Every name is confirmed with a second, separate lookup before it reaches you.',
  },
  {
    src: '/app-screenshot-search.webp',
    alt: 'LastFollow App Store screenshot: searching Instagram usernames to pick a public profile to track',
    title: 'Track one account',
    text:
      'Search a username and tap Track. For a public account there is no Instagram sign-in and no password: the daily check runs on LastFollow servers.',
  },
];

function StoreButton() {
  return (
    <a
      className="lf-store-link"
      href={appStoreUrl}
      aria-label="Download LastFollow on the App Store"
      onClick={() => trackAppDownload('ios_app_store')}
    >
      <img src="/appstore-336.png" alt="Download LastFollow on the App Store" width="168" height="56" />
    </a>
  );
}

function NavDownloadButton() {
  return (
    <a
      className="lf-nav-cta"
      href={appStoreUrl}
      aria-label="Download LastFollow on the App Store"
      onClick={() => trackAppDownload('ios_app_store_header')}
    >
      App Store
    </a>
  );
}

export default function LastFollow() {
  useEffect(() => {
    trackPageView(window.location.pathname, homeContent.title);
  }, []);

  return (
    <main className="lf-page">
      <nav className="lf-nav" aria-label="Primary navigation">
        <a className="lf-brand" href="/">
          <img src="/lastfollow-logo-80.png" alt="LastFollow app icon" width="40" height="40" />
          <span>LastFollow</span>
        </a>
        <div className="lf-navlinks">
          <a href="/how-it-works">How it works</a>
          <a href="/guides">Guides</a>
          <a href="/about">About</a>
          <a href="/support">Support</a>
        </div>
        <NavDownloadButton />
      </nav>

      <section className="lf-section lf-hero">
        <div className="lf-hero-copy">
          <p className="lf-eyebrow">The official website of the LastFollow iPhone app</p>
          <h1>{homeContent.headline}</h1>
          <p className="lf-lede">{homeContent.lead}</p>
          <div className="lf-actions">
            <StoreButton />
            <a className="lf-secondary" href="/how-it-works">
              How LastFollow works
            </a>
          </div>
          {/* Every localized page carries this line; the English home page was
              the one that did not, and it is the page most of the brand traffic
              lands on. Stated, dated and linked -- not marked up as this site's
              AggregateRating, which is Apple's data and not ours to republish
              as review schema. */}
          <p className="lf-rating">
            <a href={appStoreUrl}>
              {`${homeContent.rating.value} out of 5 from ${homeContent.rating.count} App Store ratings`}
            </a>{' '}
            {/* One string before <time>, not several adjacent text children:
                React separates adjacent text with <!-- --> in the prerendered
                HTML, the CRA build minifier strips those comments, and
                hydration then rebuilt this span on every page load. */}
            <span>
              {`(${homeContent.rating.storefronts} storefronts \u00b7 `}
              <time dateTime={homeContent.rating.fetchedAt}>{homeContent.rating.fetchedAt}</time>)
            </span>
          </p>
          <p>Free download with in-app purchases. <a href={appStoreUrl}>See current App Store pricing</a>.</p>
          <dl className="lf-proof">
            <div>
              <dt>Platform</dt>
              <dd>iPhone app</dd>
            </div>
            <div>
              <dt>App Store ID</dt>
              <dd>6746115791</dd>
            </div>
            <div>
              <dt>Official domain</dt>
              <dd>lastfollow.app</dd>
            </div>
          </dl>
        </div>

        <aside className="lf-phone" aria-label="LastFollow app preview">
          <img className="lf-app-icon" src="/lastfollow-logo-300.png" alt="LastFollow logo" width="150" height="150" />
          <h2>LastFollow: Profile Tracker</h2>
          <p>One check a day, at the hour you pick, and a notification naming who started following and who left.</p>
        </aside>
      </section>

      <section className="lf-section lf-split">
        <div>
          <p className="lf-kicker">Why choose LastFollow</p>
          <h2>An iPhone app that watches one Instagram account for you</h2>
        </div>
        <div className="lf-text-block">
          <p>
            LastFollow is built for one focused job. You pick an Instagram account, LastFollow checks it
            once a day on its own servers at an hour you choose, and a notification tells you who started
            following that account and who left. You do not have to open the app for the check to happen,
            and for a public account you do not sign in to Instagram at all. It is not a generic web
            scraper or a similarly named third-party service; the official iPhone app is published on the
            Apple App Store as LastFollow: Profile Tracker.
          </p>
          <p>
            Use the official lastfollow.app domain and App Store ID 6746115791 when checking the app. The listing name is LastFollow: Profile Tracker; check that name and app ID before downloading.
          </p>
        </div>
      </section>

      <section className="lf-section lf-grid-section">
        <div>
          <p className="lf-kicker">Feature previews</p>
          <h2>What LastFollow shows you</h2>
        </div>
        <div className="lf-preview-grid">
          {appScreenshots.map((screenshot) => (
            <article className="lf-preview-card" key={screenshot.src}>
              <img
                className="lf-screenshot"
                src={screenshot.src}
                alt={screenshot.alt}
                width="598"
                height="1300"
                loading="lazy"
              />
              <h3>{screenshot.title}</h3>
              <p>{screenshot.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="lf-section lf-answer-block" aria-labelledby="how-to-start">
        <h2 id="how-to-start">How to use LastFollow</h2>
        <p className="lf-answer">
          <strong>Before you start:</strong> the first check records the account as it is today. A comparison
          needs two points, so your first report of who started following and who left arrives with the
          check after it.
        </p>
        <ol className="lf-steps">
          {homeContent.steps.map((step, index) => (
            <li id={`step${index + 1}`} key={step.name}>
              <strong>{step.name}</strong>
              <p>{step.text}</p>
            </li>
          ))}
        </ol>
        <p><a href="/how-it-works">Read the full guide to tracking, private accounts and limits</a>.</p>
      </section>

      <section className="lf-section lf-grid-section">
        <h2>Choose the list you want to watch</h2>
        <div className="lf-card-grid">
          <article className="lf-card">
            <h3>Who started following a profile?</h3>
            <p>Track the followers list and each daily check names the accounts that joined it since the check before.</p>
            <a href="/recent-instagram-followers">Understand recent followers</a>
          </article>
          <article className="lf-card">
            <h3>Who did a profile start following?</h3>
            <p>Track the following list and each daily check names the accounts that profile added since the check before.</p>
            <a href="/how-to-see-recent-instagram-followings">Understand recent followings</a>
          </article>
          <article className="lf-card">
            <h3>Who left the list?</h3>
            <p>The same comparison names the accounts that dropped out between two checks, confirmed one by one before they are shown.</p>
            <a href="/guides/how-to-track-instagram-unfollowers-2026">Understand unfollower checks</a>
          </article>
        </div>
      </section>

      <section className="lf-section lf-split">
        <div>
          <p className="lf-kicker">How LastFollow is sure</p>
          <h2>The answer comes from an exact comparison</h2>
        </div>
        <div className="lf-text-block">
          <p>
            LastFollow keeps the list from the last check, reads the account again the next day, and works
            out the exact difference between the two. That difference is the report: the accounts that are
            in the list now and were not before, and the accounts that were there and are gone.
          </p>
          <p>
            Instagram sometimes returns a long list incomplete, so every name that comes out of the
            comparison is confirmed with a second, separate lookup before it reaches you. A name that
            cannot be confirmed is not shown. When LastFollow is not certain, it says nothing rather than
            name the wrong person.
          </p>
          <p>
            Because a comparison needs two points, the very first check is a baseline. Your first report
            arrives with the check that follows it, at the hour you chose.
          </p>
        </div>
      </section>

      <section className="lf-section lf-grid-section">
        <div>
          <p className="lf-kicker">The limits, up front</p>
          <h2>What LastFollow can and cannot watch</h2>
        </div>
        <div className="lf-use-case-grid">
          <article className="lf-use-case">
            <h3>One public and one private account</h3>
            <p>
              You can track one public account and one private account at a time. They are separate
              allowances, because the cost and the risk of each are different. Re-opening a profile you
              already track does not use another one.
            </p>
          </article>
          <article className="lf-use-case">
            <h3>Names up to 1,000 people</h3>
            <p>
              Up to 1,000 people, LastFollow names everyone who joined and everyone who left. Above that it
              follows the total instead: the daily rise and fall of the count, charted over 90 days, with no
              names collected.
            </p>
          </article>
          <article className="lf-use-case">
            <h3>Private accounts are read on your phone</h3>
            <p>
              Instagram shows a private account list only to someone who follows it, so that list is read on
              your iPhone with your own Instagram session and compared there. There is no automatic daily
              check and no notification for it; the comparison runs when you open the app. The limit is 500
              people, on one list per profile.
            </p>
          </article>
        </div>
        <p>
          Your own followers and following are read on the device too, with your Instagram session, when you
          open the app. Those lists are not sent to LastFollow servers, there is no size limit, and your own
          account never counts against either tracking allowance.
        </p>
      </section>

      <section className="lf-section lf-split">
        <div>
          <p className="lf-kicker">App identity</p>
          <h2>Find the official LastFollow app</h2>
        </div>
        <div className="lf-text-block">
          <p>
            lastfollow.app is the official website for the LastFollow iPhone app. The app is listed on the
            Apple App Store as LastFollow: Profile Tracker under App Store ID 6746115791, published by Mert
            Sen FINDICAK.
          </p>
          <p>
            LastFollow is not affiliated with Instagram, Meta or any third-party website using a similar name.
            For the official iOS app, use the App Store link on this page.
          </p>
        </div>
      </section>

      <section className="lf-section">
        <div>
          <p className="lf-kicker">Guides</p>
          <h2>Learn more about recent followers and followings</h2>
        </div>
        <ul className="lf-page-list">
          <li><a href="/guides/how-to-track-instagram-unfollowers-2026">How to track Instagram unfollowers in 2026</a></li>
          <li><a href="/guides/why-instagram-doesnt-show-recent-followers">Why Instagram does not show recent followers correctly</a></li>
          <li><a href="/guides/best-ios-apps-instagram-profile-tracking">Best iOS apps for Instagram profile tracking</a></li>
          <li><a href="/how-to-see-recent-instagram-followings">How to see recent Instagram followings</a></li>
          {/* /recent-follow drew 1,006 impressions in the 28 days to
              2026-09-02 -- more than any English page but this one -- on three
              internal links, none of them from here. */}
          <li><a href="/recent-follow">Recent follows on Instagram, checked daily</a></li>
        </ul>
      </section>

      <section className="lf-section lf-faq" id="faq">
        <h2>Common questions</h2>
        {homeContent.faqs.map((faq, index) => (
          <details key={faq.question} open={index === 0}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}
        <p>Read our <a href="/privacy">privacy policy</a> and <a href="/terms">terms</a>, or <a href="/support">contact support</a> for help with the app.</p>
      </section>

      <section className="lf-section">
        <h2>Languages</h2>
        <div className="lf-language-grid">
          {languageLinks.map(([href, label]) => (
            <a key={href} href={href} aria-current={href === '/' ? 'true' : undefined}>
              {label}
            </a>
          ))}
        </div>
      </section>

      <footer className="lf-footer">
        <p>LastFollow - Official iPhone App. App Store ID: 6746115791.</p>
        <nav aria-label="Footer navigation">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/support">Support</a>
          <a href="/about">Product facts</a>
          <a href="/sitemap.xml">Sitemap</a>
        </nav>
      </footer>
    </main>
  );
}
